import { useState, useEffect } from "react";
import {
  getActiveCompetitionsByDivision,
  getClubs,
  getFullCompetitions,
  getCompetitionsByDivisionAndSeason,
} from "shared/services/api-assistant.js";
import BoxCard from "shared/components/box/BoxCard.js";
import ChartBarClubsRanking from "shared/components/charts/ChartBarClubsRanking.js";
import { divisions } from "shared/utils/division.js";
import ButtonSelect from "shared/components/buttons/ButtonSelect.js";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { convertDictToUrlParams } from "shared/utils/url.js";
import { copyTextToClipboard } from "shared/utils/clipboard.js";
import ButtonLogin from "shared/components/buttons/ButtonLogin.js";
import LoadingSquare from "shared/components/loading/LoadingSquare.js";
import MenuPageDivisionStrength from "bars/MenuPageDivisionStrength.js";

const PageDivisionStrength = (props) => {
  const [userClubs, setUserClubs] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [competitionHistory, setCompetitionHistory] = useState(null);
  const [competitions, setCompetitions] = useState(null);
  const [competitions2, setCompetitions2] = useState(null);
  const [clubs, setClubs] = useState(null);
  const [clubs2, setClubs2] = useState(null);
  const [selectedDivisionName, setSelectedDivisionName] = useState(
    divisions.map((d) => d.name).includes(searchParams.get("division")) ? searchParams.get("division") : "Diamond",
  );
  const [selectedLeague, setSelectedLeague] = useState(searchParams.get("league") ? searchParams.get("league") : "");
  const [selectedSeasonName, setSelectedSeasonName] = useState(searchParams.get("season") || "");
  const [selectedDate, setSelectedDate] = useState(searchParams.get("date") || "");
  const [criteria, setCriteria] = useState("mmr");
  const [isComparisonOpened, setIsComparisonOpened] = useState(false);

  const [isCompetition2Loading, setIsCompetition2Loading] = useState(false);

  const fetchData = () => {
    setCompetitions(null);
    setClubs(null);

    getActiveCompetitionsByDivision({
      handleSuccess: (v) => {
        const competitionsData = v.data.getActiveCompetitionsByDivision;
        setCompetitions(competitionsData.filter((c) => c.name.includes("League")));
        const allClubs = competitionsData.flatMap((competition) => competition.participants || []);
        setClubs(allClubs);
      },
      params: {
        divisionName: selectedDivisionName,
      },
    });
  };

  const fetchCompetitionHistory = () => {
    getFullCompetitions({
      handleSuccess: (v) => {
        const allCompetitions = v.data.getCompetitions.sort((a, b) => a.season - b.season);
        setCompetitionHistory(allCompetitions);
      },
      handleError: (e) => console.log(`Error fetching competition history`, e),
      params: { name: "Diamond League" },
    });
  };

  const fetchCompetitions2 = () => {
    setClubs2(null);
    setCompetitions2(null);
    setIsCompetition2Loading(true);

    getCompetitionsByDivisionAndSeason({
      handleSuccess: (v) => {
        const competitionsData = v.data.getCompetitionsByDivisionAndSeason;
        setCompetitions2(competitionsData.filter((c) => c.name.includes("League")));
        const allClubs = competitionsData.flatMap((competition) => competition.participants || []);
        setClubs2(allClubs);
        setIsCompetition2Loading(false);
      },
      params: {
        divisionName: selectedDivisionName,
        seasonName: selectedSeasonName,
      },
      startDate: competitionHistory.filter((c) => c.seasonName === selectedSeasonName).pop().startingDate,
      endDate: new Date(
        new Date(competitionHistory.filter((c) => c.seasonName === selectedSeasonName).pop().startingDate).getTime() +
          30 * 24 * 60 * 60 * 1000,
      )
        .toISOString()
        .slice(0, 19),
    });
  };

  const getDatesOfCompetitions2 = () => {
    return [
      ...new Set(
        competitions2?.flatMap((item) =>
          item.participants.flatMap((participant) => participant.history.map((history) => history.date)),
        ) || [],
      ),
    ].sort((a, b) => new Date(a) - new Date(b));
  };

  const fetchClubs = () => {
    getClubs({
      handleSuccess: (d) => {
        let c = d.data.getClubs;
        setUserClubs(c);
      },
      handleError: console.log,
      params: { owners: [props.assistantUser.id] },
    });
  };

  useEffect(() => {
    fetchCompetitionHistory();
  }, []);

  useEffect(() => {
    navigate({
      search:
        "?" +
        convertDictToUrlParams({
          division: selectedDivisionName,
          league: selectedLeague !== "" ? selectedLeague : undefined,
        }),
    });
    fetchData();
  }, [selectedDivisionName, selectedLeague]);

  useEffect(() => {
    navigate({
      search:
        "?" +
        convertDictToUrlParams({
          division: selectedDivisionName,
          league: selectedLeague !== "" ? selectedLeague : undefined,
        }),
    });
  }, [selectedLeague]);

  useEffect(() => {
    if (props.assistantUser) fetchClubs();
  }, [props.assistantUser]);

  useEffect(() => {
    if (selectedSeasonName) fetchCompetitions2();
  }, [selectedDivisionName, selectedSeasonName]);

  return (
    <div id="PageDivisionStrength" className="h-100 w-100 d-flex flex-column">
      <div className="flex-shrink-0">
        <MenuPageDivisionStrength />
      </div>
      <div className="flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
      <div className="container-fluid h-100 px-1 px-md-4 py-4" style={{ minHeight: 0, boxSizing: "border-box" }}>
        <div className="d-flex flex-column flex-md-row h-100 w-100" style={{ minHeight: 0 }}>
          <div className="d-flex flex-column flex-md-grow-0 flex-md-basis-300">
            <BoxCard
              title={"Filters"}
              actions={
                <div className="flex-glow-0">
                  <button
                    className="btn btn-sm btn-link align-self-start"
                    onClick={() => copyTextToClipboard(window.location.href)}
                  >
                    <i className="bi bi-share-fill" />
                  </button>
                </div>
              }
              content={
                <div className="d-flex w-100 flex-column">
                  <div className="d-flex flex-row mb-1 justify-content-end">
                    <ButtonSelect
                      selected={criteria}
                      onChange={(v) => setCriteria(v)}
                      buttons={{
                        playerCount: "Nb",
                        b11: "B11",
                        b11a: "B11a",
                        b16a: "B16a",
                        ig11: "IG11",
                        mmr: "MMR",
                      }}
                    />
                  </div>

                  <div className="d-flex flex-column">
                    <select
                      className="form-control w-100 text-white mb-1"
                      value={selectedDivisionName}
                      onChange={(v) => {
                        setSelectedLeague("");
                        setSelectedDivisionName(v.target.value);
                      }}
                    >
                      {divisions.map((p) => (
                        <option value={p.name} key={p.name}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                    <select
                      className="form-control w-100 text-white mb-1"
                      value={selectedLeague}
                      onChange={(v) => setSelectedLeague(v.target.value)}
                      disabled={!competitions || competitions?.length <= 1}
                    >
                      <option value={""} key={""}>
                        All leagues
                      </option>
                      {competitions?.map((c) => (
                        <option value={c.name} key={c.name}>
                          {c.name.includes("–") ? c.name.split("–")[1].trim() : c.name}
                        </option>
                      ))}
                    </select>
                    <div className="d-flex flex-fill justify-content-end">
                      <button
                        className={"btn align-self-end btn-link"}
                        onClick={() => {
                          if (isComparisonOpened) {
                            setSelectedSeasonName("");
                            setSelectedDate("");
                          }
                          setIsComparisonOpened(!isComparisonOpened);
                        }}
                      >
                        <i class="bi bi-plus-slash-minus"></i>{" "}
                        {isComparisonOpened ? "Close comparison" : "Compare with past"}
                      </button>
                    </div>
                    {isComparisonOpened && (
                      <>
                        {competitionHistory ? (
                          <select
                            className={
                              "form-control w-auto text-white mb-1 " + (selectedSeasonName === "" ? "highlighted" : "")
                            }
                            value={selectedSeasonName}
                            onChange={(e) => {
                              if (e.target.value === "") {
                                setSelectedDate("");
                              }
                              setSelectedSeasonName(e.target.value);
                            }}
                          >
                            <option value={""} key={""}>
                              Select season
                            </option>
                            {competitionHistory?.map((c, index) => (
                              <option value={c.seasonName} key={c.seasonName}>
                                {c.seasonName}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div className="mb-1">
                            <LoadingSquare height={"40px"} />
                          </div>
                        )}
                        {!isCompetition2Loading ? (
                          <select
                            className={
                              "form-control w-auto text-white mb-1 " +
                              (selectedSeasonName !== "" && selectedDate === "" ? "highlighted" : "")
                            }
                            value={selectedDate}
                            onChange={(e) => {
                              setSelectedDate(e.target.value);
                            }}
                            disabled={competitionHistory === null || selectedSeasonName === ""}
                          >
                            <option value={""} key={""}>
                              Select date
                            </option>
                            {getDatesOfCompetitions2().map((c, index) => (
                              <option value={c} key={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <LoadingSquare height={"40px"} />
                        )}
                      </>
                    )}
                  </div>
                </div>
              }
            />
          </div>

          <BoxCard
            className={"d-flex flex-fill"}
            title={"Division strength"}
            actions={
              !props?.assistantUser && (
                <ButtonLogin
                  flowUser={props.flowUser}
                  assistantUser={props.assistantUser}
                  logout={props.logout}
                  content={
                    <button className="btn btn-sm btn-info">
                      <i className="bi bi-box-arrow-in-right lh-1 pe-1"></i>Login to see your clubs
                    </button>
                  }
                />
              )
            }
            content={
              <div className="d-flex flex-fill position-relative" style={{ minHeight: 0 }}>
                <ChartBarClubsRanking
                  clubs={
                    competitions &&
                    selectedLeague !== "" &&
                    competitions.filter((c) => c.name?.endsWith(selectedLeague))
                      ? competitions.filter((c) => c.name?.endsWith(selectedLeague)).pop().participants
                      : clubs
                  }
                  clubs2={
                    competitions2 && clubs2 && selectedSeasonName && selectedDate
                      ? selectedLeague !== ""
                        ? competitions2
                            ?.filter((c) => c.name?.endsWith(selectedLeague) && c.seasonName === selectedSeasonName)
                            .pop()
                            ?.participants?.flatMap((participant) => {
                              const history = participant.history?.find((h) => h.date === selectedDate);

                              return history ? [{ ...participant, ...history }] : [];
                            })
                        : clubs2?.flatMap((participant) => {
                            const history = participant.history?.find((h) => h.date === selectedDate);

                            return history ? [{ ...participant, ...history }] : [];
                          })
                      : []
                  }
                  criteria={criteria}
                  highlightedClubs={userClubs?.map((c) => c.id)}
                />
              </div>
            }
          />
        </div>
      </div>
      </div>
    </div>
  );
};

export default PageDivisionStrength;
